import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';

import { getSocket } from '../../utils/socket/socket';

const SocketContext = createContext( null );

/**
 * Provider component that initializes and shares a single WebSocket instance
 * across all DevPanel components.
 */
export const DevPanel_SocketProvider = ( { children } ) => {
  const { serverRouteUtility, isLocal } = useAppConfigContext();

  const socket = useMemo( () => {
    // Determine the correct base URL for the socket
    // If serverRouteUtility is relative (e.g. /v1/...), we use the current origin.
    // In local development, if serverRouteUtility is a full URL, we extract the origin.
    let baseUrl = window.location.origin;

    if( isLocal && serverRouteUtility && serverRouteUtility.startsWith( 'http' ) ){
      try {
        baseUrl = new URL( serverRouteUtility ).origin;
      }
      catch ( e ){
        // Fallback to origin
      }
    }

    return getSocket( baseUrl );
  }, [serverRouteUtility, isLocal] );

  useEffect( () => {
    // Connect the socket if it's not already connected
    if( socket && !socket.connected ){
      socket.connect();
    }

    return () => {
      socket.disconnect();
    };
  }, [socket] );

  return (
    <SocketContext.Provider value={ socket }>
      { children }
    </SocketContext.Provider>
  );
};

/**
 * Hook to use the shared DevPanel socket
 * @returns {object} The socket.io instance
 */
export const useDevPanelSocket = () => {
  const context = useContext( SocketContext );
  if( context === undefined ){
    throw new Error( 'useDevPanelSocket must be used within a DevPanel_SocketProvider' );
  }
  return context;
};
